import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Card, Typography, TextField, MenuItem, Button, Stack, IconButton,
  Chip, Divider, Alert, CircularProgress, Grid, Tabs, Tab, Tooltip,
  FormControlLabel, Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  useAdminGetHelpArticleQuery,
  useCreateHelpArticleMutation,
  useUpdateHelpArticleMutation,
  useUploadHelpImageMutation,
  useUploadHelpVideoMutation,
  HelpArticleFull,
  HelpImage,
  HelpImageMarker,
  HelpQuickStep,
} from "../../../Api/helpApi";
import useNotifier from "../../../Core/Notifier/use-notifier";
import { API_URL } from "../../../config";

const mediaUrl = (path: string): string => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  const base = String(API_URL || "").replace(/\/+$/, "");
  return `${base}/help/media/${path.replace(/^\/+/, "")}`;
};

type FormState = {
  title: string;
  slug: string;
  module: string;
  category: string;
  route_pattern: string;
  summary: string;
  body_markdown: string;
  quick_steps: HelpQuickStep[];
  images: HelpImage[];
  video_url: string;
  video_storage_path: string;
  role_visibility: string[];
  visible_to_parents: boolean;
  sort_order: number;
};

const emptyForm = (): FormState => ({
  title: "", slug: "", module: "", category: "", route_pattern: "",
  summary: "", body_markdown: "", quick_steps: [], images: [],
  video_url: "", video_storage_path: "", role_visibility: [],
  visible_to_parents: false, sort_order: 0,
});

const EditorPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const numericId = isNew ? 0 : Number(id);
  const { data, isLoading } = useAdminGetHelpArticleQuery(numericId, { skip: isNew });
  const [create, { isLoading: creating }] = useCreateHelpArticleMutation();
  const [update, { isLoading: updating }] = useUpdateHelpArticleMutation();
  const { showMessage, showErrorMessage } = useNotifier();

  const [form, setForm] = useState<FormState>(emptyForm());
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!isNew && data?.data?.article) hydrate(data.data.article, setForm);
  }, [isNew, data]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim() || !form.module.trim() || !form.category.trim()) {
      showErrorMessage("Title, module and category are required.");
      return;
    }
    try {
      if (isNew) {
        const res = await create(payloadFromForm(form)).unwrap();
        showMessage("Article created.");
        navigate(`/help-center/manage/${res.data?.article?.id ?? ""}`, { replace: true });
      } else {
        await update({ id: numericId, body: payloadFromForm(form) }).unwrap();
        showMessage("Article saved.");
      }
    } catch (e: any) {
      const errs = e?.data?.data?.errors as string[] | undefined;
      showErrorMessage(errs?.[0] ?? e?.data?.message ?? "Save failed.");
    }
  };

  if (!isNew && isLoading) return <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>;

  return (
    <Box sx={{ pb: 6, maxWidth: 1240, mx: "auto" }}>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/help-center/manage")} sx={{ textTransform: "none", fontWeight: 700, mb: 2 }}>Back</Button>
      <Card sx={{ p: 3, mb: 2, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{isNew ? "New Article" : "Edit Article"}</Typography>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating} sx={{ textTransform: "none", fontWeight: 800 }}>
            {creating || updating ? "Saving..." : "Save"}
          </Button>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Title *" size="small" value={form.title} onChange={(e) => set("title", e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Slug (URL segment, auto if empty)" size="small" value={form.slug} onChange={(e) => set("slug", e.target.value)} helperText="Lowercase letters, digits and hyphens only." />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Module *" size="small" value={form.module} onChange={(e) => set("module", e.target.value)} placeholder="e.g. Students" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Category *" size="small" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Students & Parents" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Sort order" size="small" type="number" value={form.sort_order} onChange={(e) => set("sort_order", Number(e.target.value) || 0)} />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField fullWidth label="Route pattern" size="small" value={form.route_pattern} onChange={(e) => set("route_pattern", e.target.value)} placeholder="/students/:id or /invoice/*" helperText="Matched by the contextual Help button." />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Role visibility (comma-separated privilege names)" size="small" value={form.role_visibility.join(",")} onChange={(e) => set("role_visibility", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} helperText="Blank = every user with HelpArticle_View." />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={form.visible_to_parents} onChange={(e) => set("visible_to_parents", e.target.checked)} />}
              label={
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>Visible to Parents</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    When enabled, this published article can be viewed from the Parent Portal.
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Summary" size="small" value={form.summary} onChange={(e) => set("summary", e.target.value)} inputProps={{ maxLength: 500 }} />
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
          <Tab label="Quick Guide" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label="Full Guide" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label="Screenshots" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label="Video" sx={{ textTransform: "none", fontWeight: 700 }} />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {tab === 0 && <QuickStepsEditor steps={form.quick_steps} onChange={(v) => set("quick_steps", v)} />}
          {tab === 1 && <MarkdownEditor value={form.body_markdown} onChange={(v) => set("body_markdown", v)} />}
          {tab === 2 && <ScreenshotsEditor images={form.images} onChange={(v) => set("images", v)} />}
          {tab === 3 && <VideoEditor form={form} set={set} />}
        </Box>
      </Card>
    </Box>
  );
};

function hydrate(a: HelpArticleFull, setForm: React.Dispatch<React.SetStateAction<FormState>>) {
  setForm({
    title: a.title ?? "",
    slug: a.slug ?? "",
    module: a.module ?? "",
    category: a.category ?? "",
    route_pattern: a.route_pattern ?? "",
    summary: a.summary ?? "",
    body_markdown: a.body_markdown ?? "",
    quick_steps: Array.isArray(a.quick_steps) ? a.quick_steps : [],
    images: Array.isArray(a.images) ? a.images : [],
    video_url: a.video_url ?? "",
    video_storage_path: a.video_storage_path ?? "",
    role_visibility: Array.isArray(a.role_visibility) ? a.role_visibility : [],
    visible_to_parents: !!(a as any).visible_to_parents,
    sort_order: a.sort_order ?? 0,
  });
}

function payloadFromForm(f: FormState): Partial<HelpArticleFull> {
  return {
    title: f.title.trim(),
    slug: f.slug.trim() || undefined as any,
    module: f.module.trim(),
    category: f.category.trim(),
    route_pattern: f.route_pattern.trim() || null,
    summary: f.summary.trim() || null,
    body_markdown: f.body_markdown || null,
    quick_steps: f.quick_steps,
    images: f.images,
    video_url: f.video_url.trim() || null,
    video_storage_path: f.video_storage_path.trim() || null,
    role_visibility: f.role_visibility,
    visible_to_parents: f.visible_to_parents,
    sort_order: f.sort_order || 0,
  } as any;
}

// ─────────────────────────── Quick Guide editor ───────────────────────────
const QuickStepsEditor: React.FC<{ steps: HelpQuickStep[]; onChange: (v: HelpQuickStep[]) => void }> = ({ steps, onChange }) => {
  const add = () => onChange([...steps, { number: steps.length + 1, title: "", description: "" }]);
  const del = (i: number) => onChange(steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, number: idx + 1 })));
  const update = (i: number, patch: Partial<HelpQuickStep>) => onChange(steps.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  return (
    <Stack spacing={1.5}>
      <Alert severity="info" sx={{ fontSize: 13 }}>Concise numbered instructions — displayed to end users as the primary guide.</Alert>
      {steps.map((s, i) => (
        <Card key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Chip label={s.number} color="primary" sx={{ fontWeight: 800 }} />
            <Stack spacing={1} sx={{ flex: 1 }}>
              <TextField size="small" placeholder="Optional title" value={s.title ?? ""} onChange={(e) => update(i, { title: e.target.value })} />
              <TextField size="small" multiline minRows={2} placeholder="Step description" value={s.description} onChange={(e) => update(i, { description: e.target.value })} />
            </Stack>
            <IconButton onClick={() => del(i)}><DeleteOutlineIcon color="error" /></IconButton>
          </Stack>
        </Card>
      ))}
      <Button startIcon={<AddIcon />} onClick={add} sx={{ textTransform: "none", fontWeight: 700, alignSelf: "flex-start" }}>Add step</Button>
    </Stack>
  );
};

// ─────────────────────────── Markdown editor ───────────────────────────
const MarkdownEditor: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled" }}>MARKDOWN</Typography>
      <TextField fullWidth multiline minRows={16} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Write the long-form guide in Markdown..." sx={{ mt: 0.5, "& textarea": { fontFamily: "monospace", fontSize: 13.5 } }} />
    </Grid>
    <Grid item xs={12} md={6}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled" }}>PREVIEW</Typography>
      <Box sx={{ mt: 0.5, p: 2, border: 1, borderColor: "divider", borderRadius: 1, minHeight: 380, "& p": { color: "text.secondary", lineHeight: 1.7, mb: 1.25 } }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{value || "*Preview will appear here.*"}</ReactMarkdown>
      </Box>
    </Grid>
  </Grid>
);

// ─────────────────────────── Screenshots editor + annotator ───────────────────────────
const ScreenshotsEditor: React.FC<{ images: HelpImage[]; onChange: (v: HelpImage[]) => void }> = ({ images, onChange }) => {
  const [uploadImage, { isLoading: uploading }] = useUploadHelpImageMutation();
  const { showErrorMessage } = useNotifier();
  const [active, setActive] = useState(0);

  const addFile = async (file: File) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await uploadImage(fd).unwrap();
      const next: HelpImage = { path: res.data.path, caption: "", sort: images.length, markers: [] };
      onChange([...images, next]);
      setActive(images.length);
    } catch (e: any) {
      showErrorMessage(e?.data?.message ?? "Upload failed.");
    }
  };

  const handleFilePick = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/png,image/jpeg,image/webp";
    input.onchange = () => { const f = input.files?.[0]; if (f) addFile(f); };
    input.click();
  };

  const del = (i: number) => {
    const next = images.filter((_, idx) => idx !== i).map((im, idx) => ({ ...im, sort: idx }));
    onChange(next);
    setActive(Math.max(0, Math.min(next.length - 1, active)));
  };

  const updateImage = (i: number, patch: Partial<HelpImage>) => {
    onChange(images.map((im, idx) => idx === i ? { ...im, ...patch } : im));
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ color: "text.secondary" }}>PNG, JPG or WebP up to 6 MB. Uploaded to private Help disk.</Typography>
        <Button startIcon={<UploadFileOutlinedIcon />} onClick={handleFilePick} disabled={uploading} variant="outlined" sx={{ textTransform: "none", fontWeight: 700 }}>
          {uploading ? "Uploading..." : "Upload screenshot"}
        </Button>
      </Stack>

      {images.length === 0 && <Alert severity="info" sx={{ fontSize: 13 }}>No screenshots yet — the viewer will show "Screenshot guide coming soon".</Alert>}

      {images.length > 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Stack spacing={1}>
              {images.map((im, i) => (
                <Card key={i} variant="outlined" onClick={() => setActive(i)} sx={{ p: 1, cursor: "pointer", borderColor: i === active ? "primary.main" : undefined, borderWidth: i === active ? 2 : 1 }}>
                  <Box sx={{ position: "relative", borderRadius: 1, overflow: "hidden", bgcolor: "action.hover" }}>
                    <Box component="img" src={mediaUrl(im.path)} alt="" sx={{ display: "block", width: "100%", height: 80, objectFit: "cover" }} />
                  </Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography variant="caption">#{i + 1}</Typography>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); del(i); }}><DeleteOutlineIcon fontSize="small" color="error" /></IconButton>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={9}>
            {images[active] && (
              <AnnotatorPanel
                image={images[active]}
                onChange={(patch) => updateImage(active, patch)}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Stack>
  );
};

const AnnotatorPanel: React.FC<{ image: HelpImage; onChange: (patch: Partial<HelpImage>) => void }> = ({ image, onChange }) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const markers = image.markers ?? [];

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = imgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    const next: HelpImageMarker = { number: markers.length + 1, x, y, text: "" };
    onChange({ markers: [...markers, next] });
  };

  const setMarker = (idx: number, patch: Partial<HelpImageMarker>) => {
    onChange({ markers: markers.map((m, i) => i === idx ? { ...m, ...patch } : m) });
  };
  const delMarker = (idx: number) => {
    onChange({ markers: markers.filter((_, i) => i !== idx).map((m, i) => ({ ...m, number: i + 1 })) });
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ fontSize: 13 }}>Click on the image to drop a numbered marker. Positions are stored as normalized coordinates so they remain accurate at any display size.</Alert>
      <TextField size="small" label="Caption" value={image.caption ?? ""} onChange={(e) => onChange({ caption: e.target.value })} />
      <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden", border: 1, borderColor: "divider", cursor: "crosshair" }} onClick={handleClick}>
        <Box component="img" ref={imgRef} src={mediaUrl(image.path)} alt="" sx={{ display: "block", width: "100%", height: "auto", pointerEvents: "none" }} />
        {markers.map((m, i) => (
          <Box key={i} sx={{ position: "absolute", left: `${m.x * 100}%`, top: `${m.y * 100}%`, transform: "translate(-50%,-50%)", width: 28, height: 28, borderRadius: "50%", bgcolor: "warning.main", color: "warning.contrastText", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, boxShadow: 3, border: "2px solid white", pointerEvents: "none" }}>
            {m.number}
          </Box>
        ))}
      </Box>
      {markers.length > 0 && (
        <Stack spacing={1}>
          {markers.map((m, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Chip label={m.number} color="warning" size="small" sx={{ fontWeight: 800 }} />
              <TextField size="small" fullWidth placeholder={`Describe marker ${m.number}`} value={m.text} onChange={(e) => setMarker(i, { text: e.target.value })} />
              <Tooltip title="Remove"><IconButton size="small" onClick={() => delMarker(i)}><DeleteOutlineIcon fontSize="small" color="error" /></IconButton></Tooltip>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

// ─────────────────────────── Video editor ───────────────────────────
const VideoEditor: React.FC<{ form: FormState; set: <K extends keyof FormState>(k: K, v: FormState[K]) => void }> = ({ form, set }) => {
  const [uploadVideo, { isLoading }] = useUploadHelpVideoMutation();
  const { showErrorMessage } = useNotifier();

  const handlePick = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "video/mp4,video/webm,video/quicktime";
    input.onchange = async () => {
      const f = input.files?.[0]; if (!f) return;
      try {
        const fd = new FormData(); fd.append("file", f);
        const res = await uploadVideo(fd).unwrap();
        set("video_storage_path", res.data.path);
        set("video_url", "");
      } catch (e: any) {
        showErrorMessage(e?.data?.message ?? "Video upload failed.");
      }
    };
    input.click();
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ fontSize: 13 }}>Guidance: simple task 30–90 seconds · normal workflow 1–2 minutes · complex workflow 2–5 minutes.</Alert>
      <TextField label="External video URL (YouTube, Vimeo, MP4 link)" size="small" value={form.video_url} onChange={(e) => set("video_url", e.target.value)} />
      <Divider>OR</Divider>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button startIcon={<UploadFileOutlinedIcon />} onClick={handlePick} disabled={isLoading} variant="outlined" sx={{ textTransform: "none", fontWeight: 700 }}>
          {isLoading ? "Uploading..." : "Upload MP4 / WebM"}
        </Button>
        {form.video_storage_path && (
          <Chip label={form.video_storage_path} onDelete={() => set("video_storage_path", "")} />
        )}
      </Stack>
      {(form.video_url || form.video_storage_path) && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>PREVIEW</Typography>
          <PreviewVideo form={form} />
        </Box>
      )}
    </Stack>
  );
};

const PreviewVideo: React.FC<{ form: FormState }> = ({ form }) => {
  const src = form.video_url ? form.video_url : (form.video_storage_path ? mediaUrl(form.video_storage_path) : "");
  const isEmbed = /youtube\.com|youtu\.be|vimeo\.com/i.test(src);
  if (!src) return null;
  if (isEmbed) return (
    <Box sx={{ position: "relative", pt: "56.25%", mt: 0.5 }}>
      <Box component="iframe" src={src} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, borderRadius: 2 }} allow="autoplay; encrypted-media" />
    </Box>
  );
  return <Box component="video" controls src={src} sx={{ mt: 0.5, width: "100%", borderRadius: 2, bgcolor: "black" }} />;
};

export default EditorPage;
